/**
 * Medical Slim ZERO 配下の治療ページ（RF / 脂肪冷却 / 医療用EMS）用の正式コンテンツ。
 * NOTE: 効果・数値（周波数／温度／照射条件／筋肉増強率など）・機器スペック・作用機序・料金は
 *       正式確認できていないため、推測して記載しない。未確定の情報は「—」または後から追記できる構造にする。
 *       新脂肪溶解注射ページ（/slim/injection）と同じ構成・トーンを継承する。
 */

export type SlimZeroDetailPage = {
  slug: string;
  /** HEROのPAGE TITLE（英字） */
  en: string;
  /** HEROのPAGE TITLE（日本語） */
  jp: string;
  /** メインコピー */
  heroMain: string;
  /** サブコピー */
  heroSub: string;
  heroImageAlt: string;
  /** FV画像URL（正式画像が用意されたため差し込む） */
  heroImage: string;
  /** FV画像のobject-position（人物の見え方をページごとに調整） */
  heroObjectPosition: string;
  heroNote: string;
  /** 01 ABOUT 本文 */
  about: string[];
  /** 02 RECOMMENDED FOR */
  concerns: string[];
  /** 05 FEATURES */
  features: { no: string; title: string; text: string }[];
  /** 06 METHOD（分類／施術方法／使用製剤・使用機器） */
  method: { label: string; value: string }[];
  /** 09 RISK / PRECAUTIONS */
  risk: string[];
  /** 11 FINAL CTA のLINEボタンラベル */
  ctaLabel: string;
};

const SLIM_FEATURE_NOTE = "※ 施術可能な条件については、身体の状態や診察内容によって異なります。詳しくは診察時にご相談ください。";

const SLIM_FLOW = [
  { no: "01", title: "ご予約", text: "WEB・LINE・電話からご予約ください。" },
  { no: "02", title: "カウンセリング", text: "体型や気になる部位、ご希望などを伺います。" },
  { no: "03", title: "医師の診察", text: "身体の状態や施術部位を確認し、治療の適応を判断します。" },
  { no: "04", title: "治療内容・料金のご説明", text: "施術内容、施術範囲、料金、リスクなどをご説明します。" },
  { no: "05", title: "施術", text: "治療内容に同意いただいたうえで施術を行います。" },
  { no: "06", title: "アフターケア", text: "施術後の過ごし方や注意事項をご案内します。" },
];

const SLIM_SPEC = [
  { label: "施術時間", value: "—" },
  { label: "麻酔", value: "—" },
  { label: "ダウンタイム", value: "—" },
  { label: "推奨回数", value: "—" },
  { label: "推奨間隔", value: "—" },
];

const SLIM_AREAS = ["お腹", "二の腕", "太もも", "腰まわり", "その他の気になる部位"];

/** Medical Slim ZERO の4治療（PRICEを除く相互リンク用） */
export const slimZeroTreatments = [
  { name: "新脂肪溶解注射", href: "/slim/injection" },
  { name: "RF（ラジオ波）", href: "/slim/rf" },
  { name: "脂肪冷却", href: "/slim/cooling" },
  { name: "医療用EMS", href: "/slim/ems" },
];

/**
 * Medical Slim ZERO の公式サイト定価（1回）。
 * 公式サイトには定価のみを掲載し、新規お試しモニター価格はLP側のみで案内する。
 */
export const slimZeroPriceRows = [
  { slug: "injection", name: "医療痩身注射", note: "1エリア", price: "74,800円" },
  { slug: "rf", name: "RF（ラジオ波痩身）", note: "RF（ラジオ波）", price: "55,000円" },
  { slug: "cooling", name: "医療用脂肪冷却", note: "脂肪冷却", price: "154,000円" },
  { slug: "ems", name: "医療用EMS", note: "EMS", price: "99,000円" },
];

export const slimFlowSteps = SLIM_FLOW;
export const slimSpecItems = SLIM_SPEC;
export const slimTreatmentAreas = SLIM_AREAS;
export const slimFeatureNote = SLIM_FEATURE_NOTE;

export const slimZeroDetailPages: SlimZeroDetailPage[] = [
  /* ------------------------------ RF（ラジオ波） ------------------------------ */
  {
    slug: "rf",
    en: "RF",
    jp: "ラジオ波",
    heroMain: "気になるボディラインを、医療の力で整える。",
    heroSub:
      "Medical Slim ZEROでは、RF（ラジオ波）を用いた医療痩身をご用意しています。体型や気になる部位、ご希望を確認し、医師の診察のもと治療をご提案します。",
    heroImageAlt: "渋谷美容皮膚科クリニックのRF（ラジオ波）",
    heroImage:
      "/images/site/sbdc-slim-rf-fv-v1.webp",
    heroObjectPosition: "object-center",
    heroNote: "",
    about: [
      "RF（ラジオ波）は、医療痩身で用いられる治療方法の一つです。",
      "SBDCでは、気になる部位やボディラインについてご相談いただけるMedical Slim ZEROの治療メニューとしてご案内しています。",
      "治療の適応や施術部位、施術条件については、医師が診察したうえで判断します。",
    ],
    concerns: [
      "お腹まわりが気になる",
      "二の腕が気になる",
      "太ももなど特定部位を相談したい",
      "ボディラインを整えたい",
      "Medical Slim ZEROの他治療と組み合わせて相談したい",
      "自分に合った医療痩身を相談したい",
    ],
    features: [
      {
        no: "01",
        title: "気になる部位について相談できる",
        text: "全身のダイエットとは別に、気になる部位やボディラインについて相談したい方の選択肢となる医療痩身治療です。",
      },
      {
        no: "02",
        title: "医師の診察のもと治療",
        text: "体型や施術部位、身体の状態などを確認し、医師が治療の適応を判断します。",
      },
      {
        no: "03",
        title: "他のMedical Slim ZERO治療も相談可能",
        text: "RF以外のMedical Slim ZEROの治療についてもご相談いただけます。組み合わせの可否は診察のうえでご案内します。",
      },
      {
        no: "04",
        title: "一人ひとりに合わせて治療をご提案",
        text: "ご希望だけでなく、施術部位や身体の状態も確認したうえで治療内容をご案内します。",
      },
    ],
    method: [
      { label: "分類", value: "MEDICAL SLIM ZERO" },
      { label: "施術方法", value: "RF（ラジオ波）" },
      { label: "使用機器", value: "確認中" },
    ],
    risk: [
      "施術後に、赤み、熱感、違和感などが生じる場合があります。",
      "治療に伴うリスク・副作用・禁忌については、正式情報を確認のうえ掲載します。",
      "症状や経過には個人差があります。",
    ],
    ctaLabel: "RFについてLINEで相談",
  },

  /* ------------------------------ 脂肪冷却 ------------------------------ */
  {
    slug: "cooling",
    en: "FAT COOLING",
    jp: "脂肪冷却",
    heroMain: "気になる部分に、医療痩身という選択を。",
    heroSub:
      "Medical Slim ZEROでは、脂肪冷却による医療痩身をご用意しています。気になる部位や体型について伺い、医師の診察のもと治療をご提案します。",
    heroImageAlt: "渋谷美容皮膚科クリニックの脂肪冷却",
    heroImage:
      "/images/site/sbdc-slim-cooling-fv-v1.webp",
    heroObjectPosition: "object-center",
    heroNote: "",
    about: [
      "脂肪冷却は、医療痩身で用いられる治療方法の一つです。",
      "SBDCでは、気になる部位について相談したい方へ、Medical Slim ZEROの治療選択肢の一つとしてご案内しています。",
      "施術可能な部位や適応については、診察時に確認します。",
    ],
    concerns: [
      "お腹まわりが気になる",
      "腰まわりが気になる",
      "二の腕が気になる",
      "太ももなど特定部位を相談したい",
      "外科的な痩身治療以外の方法も検討したい",
      "自分に合う医療痩身を相談したい",
    ],
    features: [
      {
        no: "01",
        title: "特定部位について相談できる",
        text: "全身ではなく、気になる特定の部位について相談したい方の選択肢としてご案内しています。",
      },
      {
        no: "02",
        title: "医師の診察のもと適応を確認",
        text: "体型や施術部位、身体の状態を確認し、医師が治療の適応を判断します。",
      },
      {
        no: "03",
        title: "Medical Slim ZEROの他治療と比較・相談可能",
        text: "Medical Slim ZEROの他治療と比較しながら、目的に合った方法をご相談いただけます。",
      },
      {
        no: "04",
        title: "体型や目的に応じて治療をご提案",
        text: "体型や目的、ご希望に応じて、無理のない治療内容をご提案します。",
      },
    ],
    method: [
      { label: "分類", value: "MEDICAL SLIM ZERO" },
      { label: "施術方法", value: "脂肪冷却" },
      { label: "使用機器", value: "確認中" },
    ],
    risk: [
      "施術後に、赤み、痛み、違和感などが生じる場合があります。",
      "治療に伴うリスク・副作用・禁忌については、正式情報を確認のうえ掲載します。",
      "症状や経過には個人差があります。",
    ],
    ctaLabel: "脂肪冷却についてLINEで相談",
  },

  /* ------------------------------ 医療用EMS ------------------------------ */
  {
    slug: "ems",
    en: "MEDICAL EMS",
    jp: "医療用EMS",
    heroMain: "ボディラインを、医療の視点から考える。",
    heroSub:
      "Medical Slim ZEROでは、医療用EMSを用いたボディケアをご用意しています。体型や気になる部位、ご希望を確認したうえで治療をご案内します。",
    heroImageAlt: "渋谷美容皮膚科クリニックの医療用EMS",
    heroImage:
      "/images/site/sbdc-slim-ems-fv-v1.webp",
    heroObjectPosition: "object-center",
    heroNote: "",
    about: [
      "医療用EMSは、Medical Slim ZEROでご案内している医療痩身メニューの一つです。",
      "SBDCでは、体型や気になる部位について伺いながら、他の医療痩身治療とあわせて治療方法をご提案します。",
      "適応や施術部位については、診察時に確認します。",
    ],
    concerns: [
      "ボディラインが気になる",
      "お腹まわりについて相談したい",
      "運動以外のボディケアも検討したい",
      "医療痩身を複数の方法から相談したい",
      "自分に合った治療を知りたい",
    ],
    features: [
      {
        no: "01",
        title: "Medical Slim ZEROの治療選択肢の一つ",
        text: "Medical Slim ZEROでご案内している医療痩身メニューの一つとしてご案内しています。",
      },
      {
        no: "02",
        title: "医師の診察のもと施術",
        text: "体型や身体の状態を確認したうえで、医師の診察のもと施術を行います。",
      },
      {
        no: "03",
        title: "他の医療痩身治療との組み合わせも相談可能",
        text: "他の医療痩身治療との組み合わせも含めてご相談いただけます。可否は診察時にご案内します。",
      },
      {
        no: "04",
        title: "一人ひとりの目的に合わせてご提案",
        text: "一人ひとりの目的やご希望に合わせて、治療内容をご提案します。",
      },
    ],
    method: [
      { label: "分類", value: "MEDICAL SLIM ZERO" },
      { label: "施術方法", value: "医療用EMS" },
      { label: "使用機器", value: "確認中" },
    ],
    risk: [
      "施術後に、筋肉の疲労感や違和感などが生じる場合があります。",
      "治療に伴うリスク・副作用・禁忌については、正式情報を確認のうえ掲載します。",
      "症状や経過には個人差があります。",
    ],
    ctaLabel: "医療用EMSについてLINEで相談",
  },
];

export function getSlimZeroDetailPage(slug: string) {
  return slimZeroDetailPages.find((page) => page.slug === slug);
}